import NextLink from '@allesauto/core/nextLink';
import { type Option } from '@allesauto/core/searchDropdown';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import {
  QuerySearchConfigurableCarsDealers,
  QuerySearchDealers,
  SchemaTypes,
} from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts } from '@allesauto/utils/consts';
import { useLazyQuery } from '@apollo/client';
import isArray from 'lodash/isArray';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import DealersList from './DealersList';
import GeoSearch from './GeoSearch';

export interface DealerFinderProps {
  onDealerSelect?: (dealer: SchemaTypes.Dealer) => void;
  beforeDealerSearch?: ({
    latLng,
    option,
  }: {
    latLng: SchemaTypes.GeoCoordinates;
    option: Option | null;
  }) => void;
  selectedSearchOption?: Option | null;
  selectedDealerId?: string | null;
  isSearchRequired?: boolean;
  optionalFilterParameters?: Omit<
    SchemaTypes.SearchDealersFilterInput,
    'geoCoordinates' | 'maxDistance'
  >;
  showPrice?: boolean;
  customFinderDealers?: SchemaTypes.Dealer[];
  showMoreTranslationKey?: string;
  hasCustomerDealerFinder?: boolean;
  isDealerListType?: boolean;
  placeholder?: string;
  isConfigurableCar?: boolean;
}

export default function DealerFinder({
  onDealerSelect = () => null,
  beforeDealerSearch = () => null,
  selectedSearchOption = null,
  selectedDealerId: selectedDealerIdDefault = null,
  isSearchRequired = false,
  optionalFilterParameters,
  showPrice = true,
  showMoreTranslationKey = 'ui.DEALER_FINDER.SHOW_MORE_LINK',
  customFinderDealers = undefined,
  hasCustomerDealerFinder = false,
  isDealerListType = true,
  placeholder,
  isConfigurableCar,
}: DealerFinderProps) {
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(
    selectedDealerIdDefault
  );
  const intl = useIntl();
  const whiteLabel = useWhiteLabel();

  const [dealers, setDealers] = useState<SchemaTypes.Dealer[] | undefined>();
  const [getDealers, { loading: standardLoading, data: standardData }] =
    useLazyQuery<Pick<SchemaTypes.Query, 'searchDealers'>>(QuerySearchDealers, {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });

  const [
    getConfigurableCarsDealers,
    { loading: configLoading, data: configData },
  ] = useLazyQuery<Pick<SchemaTypes.Query, 'searchConfigurableCarsDealers'>>(
    QuerySearchConfigurableCarsDealers,
    { fetchPolicy: 'network-only', errorPolicy: 'all' }
  );

  useEffect(() => {
    const updateDealersIfDataReady = () => {
      if (isConfigurableCar) {
        if (!configLoading && configData?.searchConfigurableCarsDealers) {
          setDealers(configData.searchConfigurableCarsDealers);
        }
      } else {
        if (!standardLoading && standardData?.searchDealers) {
          setDealers(standardData.searchDealers);
        }
      }
    };

    updateDealersIfDataReady();
  }, [
    configData,
    configLoading,
    standardData,
    standardLoading,
    isConfigurableCar,
  ]);

  useEffect(() => {
    if (customFinderDealers) {
      setDealers(customFinderDealers);
    }
  }, [customFinderDealers]);

  const onSearchClick = useCallback(
    (latLng: SchemaTypes.GeoCoordinates) => {
      if (isConfigurableCar) {
        getConfigurableCarsDealers({
          variables: {
            filter: {
              geoCoordinates: { ...latLng },
              ...optionalFilterParameters,
            },
            limit: consts.DEALER_FINDER.MAX_LIMIT_DEALERS_VISIBLE,
          },
        });
      } else {
        getDealers({
          variables: {
            filter: {
              geoCoordinates: { ...latLng },
              ...optionalFilterParameters,
            },
            limit: consts.DEALER_FINDER.MAX_LIMIT_DEALERS_VISIBLE,
          },
        });
      }
    },
    [
      getDealers,
      getConfigurableCarsDealers,
      optionalFilterParameters,
      isConfigurableCar,
    ]
  );

  const handleSearchClick = (
    latLng: SchemaTypes.GeoCoordinates,
    option: Option | null
  ) => {
    beforeDealerSearch({ latLng, option });

    !hasCustomerDealerFinder && onSearchClick(latLng);
  };

  const className = {
    wrapper: 'relative h-full flex flex-col text-p1',
    emptyState: 'mt-40px text-grey60',
  };

  const showEmptyState = useMemo(
    () =>
      (!standardLoading || !configLoading) &&
      isArray(dealers) &&
      dealers.length === 0,
    [dealers, standardLoading, configLoading]
  );

  return (
    <div className={className.wrapper}>
      <GeoSearch
        onSearchClick={handleSearchClick}
        onClear={() => setDealers(undefined)}
        selectedSearchOption={selectedSearchOption}
        isSearchRequired={isSearchRequired}
        placeholder={
          placeholder
            ? placeholder
            : translate(intl, 'ui.DEALER_FINDER.SEARCH_INPUT_PLACEHOLDER')
        }
      />

      {showEmptyState ? (
        <p className={className.emptyState}>
          {translate(intl, 'ui.DEALER_FINDER.NOT_FOUND')}&nbsp;
          {whiteLabel ? (
            translate(intl, 'ui.DEALER_FINDER.CONTACT_LINK')
          ) : (
            <NextLink
              href="/pages/kontakt"
              target={'_blank'}
              className="no-underline"
            >
              {translate(intl, 'ui.DEALER_FINDER.CONTACT_LINK')}
            </NextLink>
          )}
          &nbsp;
          {translate(intl, 'ui.DEALER_FINDER.CLOSING_TEXT')}
        </p>
      ) : (
        <DealersList
          dealers={dealers}
          loading={isConfigurableCar ? configLoading : standardLoading}
          selectedDealerId={selectedDealerId}
          onSelect={(dealer) => {
            setSelectedDealerId(dealer.id);
            onDealerSelect(dealer);
          }}
          showPrice={showPrice}
          showMoreTranslationKey={showMoreTranslationKey}
          preventLabelClick={false}
          maxVisible={consts.DEALER_FINDER.MAX_LIMIT_DEALERS_VISIBLE}
          isDealerListType={isDealerListType}
        />
      )}
    </div>
  );
}
